// Stable hash used as the globalProviderCache key in useChartData. Pure over its
// args and kept in its own module (no heavy imports) so it's unit-testable in the
// node env. dataVersion is included to invalidate on in-place mutations.
//
// #365: barColorProvider/barColorConfig MUST be part of the key — the cached value
// is the already-colored data, so two coloring methods over the same bars are
// distinct cache entries. Omitting them made a method switch (no data/TF change)
// return the stale-colored cache instead of recoloring the bars.
export function createDataHash(
  data,
  chartType,
  indicators,
  renkoConfig,
  pointFigureConfig,
  dataVersion,
  comparisonSymbols,
  periodicityParam,
  barColorProvider,
  barColorConfig,
) {
  if (!data) return null;
  // Create more detailed indicator hash including ID, render key, and config
  const indicatorHash = indicators
    .map(
      (i) =>
        `${i.id}-${i.renderKey || i.key}-${i.requireNewAxis ? "sep" : "main"}-${JSON.stringify(i.config)}-${JSON.stringify(i.appearanceConfig)}`,
    )
    .sort() // Sort to ensure consistent order
    .join("|");
  // Include data length and last data point timestamp for real-time updates
  const lastDataPoint = data[data.length - 1];
  const dataFingerprint = `${data.length}-${lastDataPoint?.date?.getTime()}-${lastDataPoint?.close}`;
  // Include renkoConfig for renko chart type to invalidate cache when settings change
  const renkoHash =
    chartType === "renko" && renkoConfig ? JSON.stringify(renkoConfig) : "";
  // Include pointFigureConfig for pointfigure chart type to invalidate cache when settings change
  const pointFigureHash =
    chartType === "pointfigure" && pointFigureConfig
      ? JSON.stringify(pointFigureConfig)
      : "";
  // Include comparison symbols hash to invalidate cache when comparisons change
  const compareHash =
    comparisonSymbols?.length > 0
      ? comparisonSymbols
          .map((s) => `${s.symbol}-${s.data?.length || 0}`)
          .join(",")
      : "";
  // #365: bar coloring method/config — distinct coloring => distinct cache entry,
  // so switching the method (no data/TF change) recolors instead of returning the
  // stale-colored cached result.
  const barColorHash = `${barColorProvider || "traditional"}-${barColorConfig ? JSON.stringify(barColorConfig) : ""}`;
  // Include dataVersion to ensure cache invalidation on in-place data mutations
  return `${dataFingerprint}-${periodicityParam}-${chartType}-${renkoHash}-${pointFigureHash}-${indicators.length}-${indicatorHash}-${compareHash}-${barColorHash}-v${dataVersion}`;
}
